import { useMemo } from "react";

import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Button, TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    FilterFn,
} from "@tanstack/react-table";

import {
    ControlProgramTagCategoryView,
    ControlProgramTagView,
} from "api/generated/model";

import * as theme from "./theme.css";

export function useControlProgramTagsListTable(
    tags: ControlProgramTagView[],
    tagCategories: ControlProgramTagCategoryView[],
    globalFilter: string,
    setGlobalFilter: (value: string) => void,
    setIsDrawerOpen: (isOpen: boolean) => void,
    setSelectedCategory: (category: CategoryOverview) => void
) {
    const { t } = useTranslation();
    const columns = useMemo(
        () => buildColumns(t, setIsDrawerOpen, setSelectedCategory),
        [setIsDrawerOpen, setSelectedCategory, t]
    );
    const data = useMemo(
        () => buildCategoryOverviewData(tags, tagCategories),
        [tags, tagCategories]
    );

    const table = useReactTable<CategoryOverview>({
        data,
        columns,
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn,
        initialState: {
            sorting: [
                {
                    id: "categoryName",
                    desc: false,
                },
            ],
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
            },
        },
        enableSortingRemoval: false,
        getRowCanExpand: (row) => row.original.tags.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    return {
        table,
    };
}

const COLUMN_ID_EXPANDER = "expander";
const COLUMN_ID_CATEGORY_NAME = "categoryName";
const COLUMN_ID_TAG_COUNT = "tagCount";

const globalFilterFn: FilterFn<CategoryOverview> = (
    row,
    _columnId,
    filterValue
) => {
    if (!filterValue) {
        return true;
    }
    const search: string = filterValue.toLowerCase();
    const { categoryName, tags } = row.original;
    return (
        categoryName.toLowerCase().includes(search) ||
        tags.some((tag) => tag.name.toLowerCase().includes(search))
    );
};

export function getVisibleTags(
    category: CategoryOverview,
    globalFilter: string
): ControlProgramTagView[] {
    if (!globalFilter) {
        return category.tags;
    }
    const search = globalFilter.toLowerCase();
    if (category.categoryName.toLowerCase().includes(search)) {
        return category.tags;
    }
    return category.tags.filter((tag) =>
        tag.name.toLowerCase().includes(search)
    );
}

function buildColumns(
    t: TFunction,
    setIsDrawerOpen: (isOpen: boolean) => void,
    setSelectedCategory: (category: CategoryOverview) => void
) {
    const columnHelper = createColumnHelper<CategoryOverview>();
    function handleDrawerOpen(category: CategoryOverview) {
        setIsDrawerOpen(true);
        setSelectedCategory(category);
    }
    return [
        columnHelper.display({
            id: COLUMN_ID_EXPANDER,
            header: () => null,
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: ({ row }) => (
                <Button
                    size="small"
                    onClick={row.getToggleExpandedHandler()}
                    disabled={!row.getCanExpand()}
                    className={theme.expanderButton}
                >
                    {row.getIsExpanded() ? <MinusOutlined /> : <PlusOutlined />}
                </Button>
            ),
        }),
        columnHelper.accessor(COLUMN_ID_CATEGORY_NAME, {
            header: t("ControlProgramTagList_CategoryColumn"),
            size: TST_COLUMN_SIZE.XXL,
            cell: (info) => {
                return (
                    <a onClick={() => handleDrawerOpen(info.row.original)}>
                        {info.getValue()}
                    </a>
                );
            },
        }),
        columnHelper.accessor(COLUMN_ID_TAG_COUNT, {
            header: t("ControlProgramTagList_TagsColumn"),
            size: TST_COLUMN_SIZE.XXL,
            cell: (info) => info.getValue(),
        }),
    ];
}

export interface CategoryOverview {
    categoryName: string;
    tagCount: number;
    tags: ControlProgramTagView[]; // Sub-rows live here
}

function buildCategoryOverviewData(
    tags: ControlProgramTagView[],
    tagCategories: ControlProgramTagCategoryView[]
): CategoryOverview[] {
    // Group tags by category ID
    const tagsByCategory = tags.reduce<Record<string, ControlProgramTagView[]>>(
        (acc, tag) => {
            if (!acc[tag.controlProgramTagCategoryId]) {
                acc[tag.controlProgramTagCategoryId] = [];
            }
            acc[tag.controlProgramTagCategoryId].push(tag);
            return acc;
        },
        {}
    );

    // Map categories to include the number of tags
    return tagCategories.map((category) => ({
        categoryName: category.name,
        tagCount: tagsByCategory[category.id]?.length || 0,
        tags: tagsByCategory[category.id] || [],
    }));
}
